import { useMutation, useApolloClient } from '@apollo/react-hooks';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  EXPORT_MEMBERS,
  MEMBERS_EXPORT_STATUS,
  GET_MEMBERS_EXPORT_DOWNLOAD_URL,
} from '../gql/exportMembers';

const useMembersExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const subscriptionRef = useRef(null);

  const client = useApolloClient();

  // Export mutation
  const [exportMembersMutation] = useMutation(EXPORT_MEMBERS);

  // Start export function
  const startExport = useCallback(
    async (params = {}, format = 'CSV') => {
      try {
        setIsExporting(true);

        // Get user's timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Build the input object with format and timezone
        const input = {
          format: format.toLowerCase(), // "csv" or "pdf"
          timezone,
        };

        // Add account_id if provided
        if (params.accountId) {
          input.account_id = params.accountId;
        }

        // Add year filter
        if (params.year) {
          input.year = params.year;
        }

        // Add ticket/type filter
        if (params.typeId) {
          input.ticket_id = Number(params.typeId);
        }

        // Add division filter
        if (params.division) {
          input.division = params.division;
        }

        // Add search filter
        if (params.search) {
          input.queryString = params.search;
        }

        // Add date range filters
        if (params.startDate) {
          input.start_date = params.startDate;
        }
        if (params.endDate) {
          input.end_date = params.endDate;
        }

        // Add type (membership or registration)
        if (params.type) {
          input.type = params.type;
        }

        // Execute the mutation
        const { data } = await exportMembersMutation({
          variables: {
            input,
          },
        });

        if (data?.getAccountAnnualsReport?.jobId) {
          setCurrentJobId(data.getAccountAnnualsReport.jobId);
          return data.getAccountAnnualsReport.jobId;
        } else {
          throw new Error('No job ID returned from export');
        }
      } catch (error) {
        setIsExporting(false);
        throw error;
      }
    },
    [exportMembersMutation]
  );

  // Subscribe to export progress
  const subscribeToProgress = useCallback(
    (jobId, callbacks = {}) => {
      const { onComplete, onError, onProgress } = callbacks;

      // Clean up any existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }

      // Create new subscription
      subscriptionRef.current = client
        .subscribe({
          query: MEMBERS_EXPORT_STATUS,
          variables: { jobId },
        })
        .subscribe({
          next: ({ data }) => {
            const status = data?.accountAnnualsExportStatus;

            if (!status) return;

            // Call progress callback if provided
            if (onProgress && status.progress !== null) {
              onProgress(status);
            }

            // Handle completion
            if (status.status === 'COMPLETED') {
              setIsExporting(false);
              if (onComplete) {
                onComplete(status);
              }
              // Clean up subscription after completion
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }

            // Handle failure
            if (status.status === 'FAILED') {
              setIsExporting(false);
              if (onError) {
                onError(new Error(status.error || 'Export failed'));
              }
              // Clean up subscription after failure
              if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
              }
            }
          },
          error: (error) => {
            setIsExporting(false);
            if (onError) {
              onError(error);
            }
            // Clean up subscription on error
            if (subscriptionRef.current) {
              subscriptionRef.current.unsubscribe();
              subscriptionRef.current = null;
            }
          },
        });

      return subscriptionRef.current;
    },
    [client]
  );

  // Get download URL
  const getDownloadUrl = useCallback(
    async (jobId) => {
      try {
        const { data } = await client.query({
          query: GET_MEMBERS_EXPORT_DOWNLOAD_URL,
          variables: { jobId: jobId || currentJobId },
          fetchPolicy: 'network-only', // Always get fresh URL
        });

        return data?.getAccountAnnualsExportDownloadUrl;
      } catch (error) {
        throw error;
      }
    },
    [client, currentJobId]
  );

  // Clean up subscription on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  return {
    startExport,
    subscribeToProgress,
    getDownloadUrl,
    isExporting,
    currentJobId,
  };
};

export default useMembersExport;
